// SNIGJ - API Client

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance - Using Bearer tokens, not cookies
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API - Note: login doesn't need withCredentials as we use Bearer tokens
export const authAPI = {
  login: (credentials) => axios.post(`${API_BASE}/auth/login`, credentials),
  register: (userData) => api.post('/auth/register', userData),
  // Google login accepts either {credential} for ID token or {access_token, email, name, picture} for popup flow
  googleLogin: (data) => axios.post(`${API_BASE}/auth/google`, data),
  processSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout')
};

// Cases API
export const casesAPI = {
  list: (params) => api.get('/cases', { params }),
  get: (caseId) => api.get(`/cases/${caseId}`),
  create: (data) => api.post('/cases', data),
  update: (caseId, data) => api.put(`/cases/${caseId}`, data),
  delete: (caseId) => api.delete(`/cases/${caseId}`)
};

// Prisoners API
export const prisonersAPI = {
  list: (params) => api.get('/prisoners', { params }),
  get: (prisonerId) => api.get(`/prisoners/${prisonerId}`),
  create: (data) => api.post('/prisoners', data),
  update: (prisonerId, data) => api.put(`/prisoners/${prisonerId}`, data)
};

// Documents API
export const documentsAPI = {
  list: (params) => api.get('/documents', { params }),
  upload: (formData) => api.post('/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  get: (documentId) => api.get(`/documents/${documentId}`)
};

// Payments API
export const paymentsAPI = {
  list: (params) => api.get('/payments', { params }),
  create: (data) => api.post('/payments/create', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`)
};

// Border Control API
export const borderAPI = {
  list: (params) => api.get('/border-alerts', { params }),
  create: (data) => api.post('/border-alerts', data)
};

// Clemency API
export const clemencyAPI = {
  filterCandidates: (filters) => api.post('/clemency/filter', filters)
};

// Audit Logs API
export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params })
};

// Admin API
export const adminAPI = {
  listUsers: (params) => api.get('/admin/users', { params }),
  updateUserRole: (userId, data) => api.put(`/admin/users/${userId}/role`, data)
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getAnalytics: (period = 'week') => api.get('/dashboard/analytics', { params: { period } })
};

// Admin API for notifications
export const notificationsAPI = {
  sendNotification: (data) => api.post('/admin/notifications', data),
  createFee: (data) => api.post('/admin/judicial-fees', data),
  searchCitizens: (q) => api.get('/admin/search-citizens', { params: { q } })
};

// Reference Data API
export const referenceAPI = {
  getRoles: () => api.get('/reference/roles'),
  getClassifications: () => api.get('/reference/classifications'),
  getCaseTypes: () => api.get('/reference/case-types'),
  getCaseStatus: () => api.get('/reference/case-status'),
  getPrisonerStatus: () => api.get('/reference/prisoner-status'),
  getTerritorialLevels: () => api.get('/reference/territorial-levels'),
  getPrisons: () => api.get('/prisons')
};

export default api;
